import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { RequireAuth, RequirePermission } from '@/auth/guards';
import { LoginPage } from '@/auth/LoginPage';
import type { Permission } from '@/auth/permissions';
import { PlaceholderPage } from '@/shared/components/PlaceholderPage';
import { NotFoundPage, RouteErrorPage } from '@/shared/components/StatusPages';
import { AppShell } from './layout/AppShell';

/** Screen planned for a later phase (docs §24) — replace `element` with a lazy feature route when implemented. */
const planned = (path: string, titleKey: string, phase: number, permission?: Permission): RouteObject => {
  const page = <PlaceholderPage titleKey={titleKey} phase={phase} />;
  return { path, element: permission ? <RequirePermission permission={permission}>{page}</RequirePermission> : page };
};

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/customers" replace /> },
          // Customers (phase 3) — code-split per feature
          {
            path: 'customers',
            lazy: () => import('@/features/customers/CustomerListPage').then((m) => ({ Component: m.CustomerListPage })),
          },
          {
            path: 'customers/:id',
            lazy: () => import('@/features/customers/CustomerDetailPage').then((m) => ({ Component: m.CustomerDetailPage })),
          },
          planned('dashboards/transaction-efficiency', 'transactionEfficiency', 9),
          planned('dashboards/transaction-case-statistics', 'transactionCaseStatistics', 9),
          planned('dashboards/customer-risk', 'customerRiskOverview', 9),
          planned('screening/ad-hoc', 'adHocScreening', 5, 'screening.run'),
          planned('cases/customer', 'customerCases', 8),
          planned('cases/transaction', 'transactionCases', 8),
          planned('config/settings', 'settings', 9, 'config.view'),
          planned('config/risk-rating', 'customerRiskRating', 6, 'config.view'),
          planned('config/workflows', 'workflows', 8, 'config.view'),
          planned('config/rules', 'ruleManager', 7, 'config.view'),
          planned('config/lists', 'listManager', 9, 'config.view'),
          {
            path: 'config/data-feeds',
            lazy: () =>
              import('@/features/dataFeeds/DataFeedsPage').then((m) => ({
                Component: () => (
                  <RequirePermission permission="dataFeeds.view">
                    <m.DataFeedsPage />
                  </RequirePermission>
                ),
              })),
          },
          {
            path: 'intelligence/watchlist-sources',
            lazy: () => import('@/features/watchlist/WatchlistSourcesPage').then((m) => ({ Component: m.WatchlistSourcesPage })),
          },
          {
            path: 'intelligence/watchlist-search',
            lazy: () => import('@/features/watchlist/WatchlistSearchPage').then((m) => ({ Component: m.WatchlistSearchPage })),
          },
          planned('intelligence/transactions', 'transactions', 7),
          planned('intelligence/audit-log', 'auditLog', 9, 'audit.view'),
          {
            path: 'admin/users',
            lazy: () =>
              import('@/features/users/UsersPage').then((m) => ({
                Component: () => (
                  <RequirePermission permission="users.manage">
                    <m.UsersPage />
                  </RequirePermission>
                ),
              })),
          },
          {
            path: 'platform/tenants',
            lazy: () =>
              import('@/features/platform/TenantsPage').then((m) => ({
                Component: () => (
                  <RequirePermission permission="platform.manage">
                    <m.TenantsPage />
                  </RequirePermission>
                ),
              })),
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
